import { inject, Injectable } from '@angular/core';
import { StorageRepository } from '@okcargo-apps/shared-domain';
import { ICredentials } from '@okcargo-apps/shared-domain-ionic';
import { BehaviorSubject, finalize, map, Observable, shareReplay, tap } from 'rxjs';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class JwtService {
  private readonly storageService = inject(StorageRepository);
  private readonly apiService = inject(ApiService);

  private readonly _refreshTokenUrl = '/service/command-processor/api/auth/refresh-token';
  private readonly _loginUrl = '/service/command-processor/api/auth/token';
  private readonly _logoutUrl = '/service/command-processor/api/auth/logout';
  private readonly _tokenLoginUrl = '/service/command-processor/api/auth/token/impersonate/';

  private readonly accessTokenSubject = new BehaviorSubject<string>('');
  private readonly refreshTokenSubject = new BehaviorSubject<string>('');
  private readonly usernameSubject = new BehaviorSubject<string>('');
  private readonly userDataReceivedSubject = new BehaviorSubject<boolean>(false);

  /** Refresh in flight, shared by every 401 that arrives meanwhile. */
  private refreshTokenRequest$?: Observable<any>;

  // constructor() {}

  public get accessToken(): string {
    return this.accessTokenSubject.getValue();
  }

  public get refreshToken(): string {
    return this.refreshTokenSubject.getValue();
  }

  public get username(): string {
    return this.usernameSubject.getValue();
  }

  public get userDataReceived$(): Observable<boolean> {
    return this.userDataReceivedSubject;
  }

  public setAccessToken(token: string): void {
    this.accessTokenSubject.next(token);
    this.storageService.create('accessToken', token);
  }

  public setRefreshToken(token: string): void {
    this.refreshTokenSubject.next(token);
    this.storageService.create('refreshToken', token);
  }

  public setUsername(username: string): void {
    this.usernameSubject.next(username);
    this.storageService.create('username', username);
  }

  public setUserDataReceived(value: boolean): void {
    // Only emit on a real change: consumers refresh data on every emission, so emitting the same
    // value again (e.g. a verification per navigation) would refresh everything for nothing.
    if (this.userDataReceivedSubject.getValue() === value) {
      return;
    }

    this.userDataReceivedSubject.next(value);
  }

  public clearTokens(): void {
    this.setAccessToken('');
    this.setRefreshToken('');
    // Emitted directly (not through setUserDataReceived) so clearing the session is always notified,
    // even when the session state was already `false`: consumers drop the stored account with it.
    this.userDataReceivedSubject.next(false);
    this.storageService.delete('accessToken');
    this.storageService.delete('refreshToken');
    this.storageService.delete('fcmToken');
  }

  public requestNewAuthToken$(): Observable<any> {
    // Single-flight: the concurrent 401s share one refresh, so they cannot set and clear the session
    // alternately (which bounced the app between the dashboard and the login).
    this.refreshTokenRequest$ ??= this.apiService
      .post(
        this._refreshTokenUrl,
        {
          refreshToken: this.refreshToken,
        },
        {},
        false,
      )
      .pipe(
        // TODO review this, type
        tap((response: any) => {
          this.setAccessToken(response.access_token || response.accessToken);
          this.setRefreshToken(response.refresh_token || response.refreshToken);
        }),
        map((response: any) => {
          return response.access_token || response.accessToken;
        }),
        finalize(() => {
          this.refreshTokenRequest$ = undefined;
        }),
        shareReplay(1),
      );

    return this.refreshTokenRequest$;
  }

  public login$(credentials: ICredentials): Observable<any> {
    this.setUsername(credentials.username);
    return this.apiService.post(
      this._loginUrl,
      credentials,
      {
        observe: 'body',
      },
      false,
    );
  }

  public validateToken$(token: string): Observable<any> {
    return this.apiService.post(
      this._tokenLoginUrl + token,
      {},
      {
        observe: 'body',
      },
      false,
    );
  }

  // TODO not work in new endpoints, ¿remove token? ¿remove storage?
  public logout$(): Observable<any> {
    return this.apiService.post(this._logoutUrl, null);
    // if (!this.apiService.newEndpointExists) return this.apiService.post(this._logoutUrl, null);
    // return of(null);
  }
}
